import { useState, useRef, useEffect } from "react"
import { motion } from "framer-motion"

import { ReportModal } from "./ReportModal"
import { BracketMatch } from "./BracketMatch"
import { generateRounds } from "./utils"

// Dibujo de líneas con SVG
export const Connector = ({ fromRect, toRect, slot }) => {
  if (!fromRect || !toRect) return null

  const startX = fromRect.right
  const startY = fromRect.top + fromRect.height / 2

  const endX = toRect.left
  const endY = slot === "top" ? toRect.top + toRect.height / 4 : toRect.bottom - toRect.height / 4

  const midX = (startX + endX) / 2

  return (
    <path
      d={`M ${startX} ${startY} H ${midX} V ${endY} H ${endX}`}
      stroke="#9ca3af"
      strokeWidth="2"
      fill="none"
    />
  )
}

export default function Bracket({ playersCount = 4 }) {
  const [modalMatch, setModalMatch] = useState(null)

  const closeReportModal = () => setModalMatch(null)

  const [rounds, setRounds] = useState(generateRounds(playersCount))
  const containerRef = useRef(null)
  const matchRefs = useRef([])

  const getWinner = (match) => {
    if (match.player1.score > match.player2.score) return match.player1
    if (match.player2.score > match.player1.score) return match.player2
    return null // 👈 si empate o sin definir
  }

  const handleUpdateScore = (playerId, newScore) => {
    setRounds((prev) => {
      const updated = prev.map((round) =>
        round.map((match) => {
          if (match.player1.id === playerId) {
            return { ...match, player1: { ...match.player1, score: newScore } }
          }
          if (match.player2.id === playerId) {
            return { ...match, player2: { ...match.player2, score: newScore } }
          }
          return match
        }),
      )

      // Recalcular ganadores y propagar
      updated.forEach((round, rIdx) => {
        if (rIdx < updated.length - 1) {
          round.forEach((match, mIdx) => {
            const winner = getWinner(match)

            const nextIndex = Math.floor(mIdx / 2)
            const slot = mIdx % 2 === 0 ? "player1" : "player2"

            updated[rIdx + 1][nextIndex][slot] = winner
              ? { ...winner }
              : { id: crypto.randomUUID(), name: "Pendiente", score: 0 }
          })
        }
      })

      return [...updated]
    })
  }

  const openReportModal = (match) => {
    // Marcar el match como editable
    setRounds((prev) =>
      prev.map((round) => round.map((m) => (m === match ? { ...m, editable: true } : m))),
    )
    setModalMatch(match)
  }

  const handleConfirmReport = (score1, score2) => {
    setRounds((prev) =>
      prev.map((round) =>
        round.map((m) => {
          if (m === modalMatch) {
            const winner = score1 > score2 ? m.player1 : m.player2
            const newMatch = {
              ...m,
              player1: { ...m.player1, score: score1 },
              player2: { ...m.player2, score: score2 },
              reported: true,
              editable: false, // bloquear inputs tras reportar
            }

            // Propagar al siguiente round
            const roundIdx = prev.indexOf(round)
            const matchIdx = round.indexOf(m)
            if (roundIdx < prev.length - 1) {
              const nextIndex = Math.floor(matchIdx / 2)
              const slot = matchIdx % 2 === 0 ? "player1" : "player2"
              prev[roundIdx + 1][nextIndex][slot] = { ...winner, score: 0 }
            }

            return newMatch
          }
          return m
        }),
      ),
    )

    setModalMatch(null)
  }

  const [rects, setRects] = useState([])

  useEffect(() => {
    if (containerRef.current) {
      const allRects = matchRefs.current.map((ref) =>
        ref?.current ? ref.current.getBoundingClientRect() : null,
      )
      setRects(allRects)
    }
  }, [rounds])

  return (
    <div ref={containerRef} className="relative overflow-x-auto py-6">
      {/* SVG detrás de los matches */}
      <svg className="absolute top-0 left-0 w-full h-full pointer-events-none z-0">
        {rounds.map((matches, roundIndex) =>
          matches.map((_, i) => {
            if (roundIndex === rounds.length - 1) return null

            const fromIdx =
              matches.slice(0, i + 1).reduce((acc, _, idx) => acc + 1, 0) +
              rounds.slice(0, roundIndex).reduce((acc, r) => acc + r.length, 0) -
              1

            const toRoundStart = rounds
              .slice(0, roundIndex + 1)
              .reduce((acc, r) => acc + r.length, 0)

            const toIdx = toRoundStart + Math.floor(i / 2)

            return (
              <Connector
                key={`${roundIndex}-${i}`}
                fromRect={rects[fromIdx]}
                toRect={rects[toIdx]}
                slot={i % 2 === 0 ? "top" : "bottom"}
              />
            )
          }),
        )}
      </svg>

      {/* Brackets */}
      <div className="flex gap-16 relative z-10">
        {rounds.map((matches, roundIndex) => (
          <motion.div
            key={roundIndex}
            className="flex flex-col items-center relative"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: roundIndex * 0.2 }}
          >
            <h2 className="font-bold mb-6">
              {roundIndex + 1 === rounds.length ? "Final" : `Ronda ${roundIndex + 1}`}
            </h2>

            {matches.map((match, i) => {
              // eslint-disable-next-line react-hooks/rules-of-hooks
              const ref = useRef(null)
              matchRefs.current.push(ref)

              const baseSpacing = 40
              const blockSize = Math.pow(2, roundIndex)
              const matchHeight = baseSpacing * 2
              const groupHeight = blockSize * matchHeight

              return (
                <div
                  className="relative group"
                  key={i}
                  style={{
                    marginTop:
                      i === 0 ? groupHeight / 2 - matchHeight / 2 : groupHeight - matchHeight,
                  }}
                >
                  <BracketMatch
                    match={match}
                    onUpdateScore={handleUpdateScore}
                    onReport={() => openReportModal(match)}
                    innerRef={ref}
                  />

                  {modalMatch && (
                    <ReportModal
                      isOpen={!!modalMatch}
                      onClose={closeReportModal}
                      match={modalMatch}
                      onConfirm={handleConfirmReport} // 👈 Aquí se asigna
                    />
                  )}
                </div>
              )
            })}
          </motion.div>
        ))}
      </div>
    </div>
  )
}
